import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getVoicePartDisplay, getRoleDisplayName } from '../utils/permissionUtils';
import '../styles/user-bio-dialog.css';

const UserBioDialog = ({ isOpen, onClose, userId, userName, userEmail }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserInfo();
    }
  }, [isOpen, userId]);

  const loadUserInfo = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=getAccount&userId=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      } else if (response.status === 404) {
        // User account doesn't exist yet, use provided info
        setUserInfo({
          displayName: userName || '',
          email: userEmail || '',
          voicePart: null,
          bio: null,
          role: 'visitor',
          createdAt: null,
          lastLogin: null
        });
      } else {
        throw new Error('Failed to load user information');
      }
    } catch (error) {
      console.error('Failed to load user info:', error);
      setError(error.message);
      // Fallback to provided info
      setUserInfo({
        displayName: userName || '',
        email: userEmail || '',
        voicePart: null,
        bio: null,
        role: 'visitor',
        createdAt: null,
        lastLogin: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-bio-overlay" onClick={handleOverlayClick}>
      <div className="user-bio-dialog">
        <div className="user-bio-header">
          <h3>{t('userBio.title')}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="user-bio-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>{t('userBio.loading')}</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{t('userBio.errorLoading')}</p>
              <button onClick={loadUserInfo} className="retry-button">
                {t('common.retry')}
              </button>
            </div>
          ) : userInfo ? (
            <>
              {/* User Profile Section */}
              <div className="user-profile-section">
                <div className="user-avatar-large">
                  {userInfo.profilePhotoUrl ? (
                    <img 
                      src={userInfo.profilePhotoUrl} 
                      alt={userInfo.displayName || userInfo.email}
                      className="profile-image"
                    />
                  ) : (
                    <span className="profile-initials">
                      {getInitials(userInfo.displayName || userInfo.email)}
                    </span>
                  )}
                </div>
                
                <div className="user-main-info">
                  <h4 className="user-display-name">
                    {userInfo.displayName || t('userBio.noName')}
                  </h4>
                  <p className="user-email-info">{userInfo.email}</p>
                  
                  <div className="user-badges">
                    <span className={`role-badge role-${userInfo.role}`}>
                      {getRoleDisplayName(userInfo.role)}
                    </span>
                    {userInfo.voicePart && (
                      <span 
                        className="voice-part-badge" 
                        style={{color: getVoicePartDisplay(userInfo.voicePart).color}}
                      >
                        {getVoicePartDisplay(userInfo.voicePart).icon} {getVoicePartDisplay(userInfo.voicePart).name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bio Section */}
              <div className="user-bio-section">
                <h5>{t('userBio.aboutUser')}</h5>
                {userInfo.bio ? (
                  <div className="bio-content">
                    <p>{userInfo.bio}</p>
                  </div>
                ) : (
                  <div className="no-bio">
                    <p>{t('userBio.noBio')}</p>
                  </div>
                )}
              </div>

              {/* Additional Info */}
              <div className="user-details-section">
                <div className="detail-grid">
                  {userInfo.voicePart && (
                    <div className="detail-item">
                      <span className="detail-label">{t('userBio.voicePart')}:</span>
                      <span className="detail-value">
                        {getVoicePartDisplay(userInfo.voicePart).icon} {getVoicePartDisplay(userInfo.voicePart).name}
                      </span>
                    </div>
                  )}
                  
                  <div className="detail-item">
                    <span className="detail-label">{t('userBio.role')}:</span>
                    <span className="detail-value">{getRoleDisplayName(userInfo.role)}</span>
                  </div>
                  
                  {formatDate(userInfo.createdAt) && (
                    <div className="detail-item">
                      <span className="detail-label">{t('userBio.memberSince')}:</span>
                      <span className="detail-value">{formatDate(userInfo.createdAt)}</span>
                    </div>
                  )}
                  
                  {formatDate(userInfo.lastLogin) && (
                    <div className="detail-item">
                      <span className="detail-label">{t('userBio.lastActive')}:</span>
                      <span className="detail-value">{formatDate(userInfo.lastLogin)}</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default UserBioDialog;